import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Chip,
  Grid,
  alpha,
  useTheme,
  InputAdornment,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DomainIcon from "@mui/icons-material/Domain";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import {
  StationForm,
  StationFormData,
  FormErrors,
  EMPTY_FORM,
  validateForm,
} from "./StationForm";
import { useKardaAdmin } from "./KardaAdminContext";

const KardaAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    showError,
    showSuccess,
    snackBarMessage,
    snackBarMode,
    handleCloseSnackbar,
  } = useErrorSnackbar();

  const { stations, isLoadingStations, fetchStations, createStation } =
    useKardaAdmin();

  const [searchTerm, setSearchTerm] = useState("");

  // Dialog state
  const [openCreate, setOpenCreate] = useState(false);
  const [formData, setFormData] = useState<StationFormData>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchStations().catch((err) => {
      if (isAxiosError(err)) {
        showError(err.response?.data?.message || err.message);
      } else {
        showError("An unexpected error occurred fetching stations.");
      }
    });
  }, [fetchStations, showError]);

  const handleCloseCreate = () => {
    setOpenCreate(false);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  };

  const handleFieldChange = (field: keyof StationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCreateStation = async () => {
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsCreating(true);
    try {
      await createStation(formData);
      handleCloseCreate();
      showSuccess("Station created successfully.");
    } catch (err) {
      if (isAxiosError(err)) {
        showError(err.response?.data?.message || err.message);
      } else {
        showError("An unexpected error occurred creating station.");
      }
    } finally {
      setIsCreating(false);
    }
  };

  const stationList = stations ?? [];

  const counts = useMemo(() => {
    const total = stationList.length;
    const active = stationList.filter(
      (s) => s.station_status === "active",
    ).length;
    const inactive = stationList.filter(
      (s) => s.station_status === "inactive",
    ).length;
    return { total, active, inactive };
  }, [stationList]);

  const isValidForm = useMemo(() => {
    return Object.keys(validateForm(formData)).length === 0;
  }, [formData]);

  const filteredStations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return stationList;
    return stationList.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        s.id.toLowerCase().includes(term) ||
        (s.subscription_tier &&
          s.subscription_tier.toLowerCase().includes(term)),
    );
  }, [stationList, searchTerm]);

  return (
    <Container maxWidth="lg" sx={{ pb: 4 }}>
      <SnackBarMessage
        message={snackBarMessage}
        mode={snackBarMode}
        handleClose={handleCloseSnackbar}
        horizontalAnchor="center"
        verticalAnchor="top"
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box />
        <Button
          onClick={() => setOpenCreate(true)}
          variant="contained"
          startIcon={<DomainIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 0.75,
            ml: 2,
            transition: "all 0.2s ease-in-out",
            boxShadow: "none",
            flexShrink: 0,
            color: "#fff",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: theme.shadows[2],
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            },
          }}
        >
          Create New Station
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Total Stations
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {counts.total}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                display: "flex",
              }}
            >
              <DomainIcon fontSize="medium" />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Active Stations
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "success.main" }}
              >
                {counts.active}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: "success.main",
                display: "flex",
              }}
            >
              <CheckCircleOutlineIcon fontSize="medium" />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Inactive Stations
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "warning.main" }}
              >
                {counts.inactive}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: "warning.main",
                display: "flex",
              }}
            >
              <HighlightOffIcon fontSize="medium" />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Search organizations..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearchTerm("")}
                    size="small"
                    edge="end"
                    aria-label="clear search"
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 320 },
              "& .MuiOutlinedInput-root": {
                bgcolor: "background.paper",
                borderRadius: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                },
                "&.Mui-focused": {
                  bgcolor: "background.paper",
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              },
            }}
          />
        </Box>

        {isLoadingStations ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: alpha(theme.palette.divider, 0.04) }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tier</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Seats</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={1}
                      >
                        <DomainIcon
                          sx={{
                            fontSize: 40,
                            color: "text.secondary",
                            opacity: 0.5,
                          }}
                        />
                        <Typography color="text.secondary">
                          No stations found.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStations.map((station) => (
                    <TableRow
                      key={station.id}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell sx={{ fontWeight: 500, p: 0 }}>
                        <ButtonBase
                          onClick={() =>
                            navigate(`/karda-admin/stations/${station.id}`)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/karda-admin/stations/${station.id}`);
                            }
                          }}
                          aria-label={`Open station ${station.name}`}
                          sx={{
                            width: "100%",
                            display: "block",
                            textAlign: "left",
                            px: 2,
                            py: 1.5,
                            cursor: "pointer",
                            fontWeight: "inherit",
                            fontSize: "inherit",
                            fontFamily: "inherit",
                            color: "inherit",
                            "&.Mui-focusVisible": {
                              outline: `2px solid`,
                              outlineColor: "primary.main",
                              outlineOffset: -2,
                            },
                          }}
                        >
                          {station.name}
                        </ButtonBase>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "text.secondary",
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                        }}
                      >
                        {station.id}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={station.station_status.toUpperCase()}
                          color={
                            station.station_status === "active"
                              ? "success"
                              : "default"
                          }
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            px: 0.5,
                            letterSpacing: "0.02em",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={station.subscription_tier || "N/A"}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>{station.seat_limit ?? "N/A"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={openCreate}
        onClose={handleCloseCreate}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 3,
            px: 4,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
            }}
          >
            <AddIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Create New Station
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Set up a new organization and configure its settings
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <StationForm
            formData={formData}
            formErrors={formErrors}
            handleFieldChange={handleFieldChange}
            disabled={isCreating}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 4,
            pb: 4,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseCreate}
            disabled={isCreating}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateStation}
            variant="contained"
            disabled={isCreating || !isValidForm}
            startIcon={
              isCreating ? <CircularProgress size={18} color="inherit" /> : null
            }
            sx={{ borderRadius: 2, px: 3 }}
          >
            {isCreating ? "Creating..." : "Create Station"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KardaAdminPage;
